// OWNED BY the tasks tab agent (created under detail/tabs/tasks/).
//
// Single data hook shared by the tab body AND the tab-strip badge:
//   - live source: CalendarContext's onSnapshot on the user's own calendar,
//     IF some surface already started it (the tasks tab body on mount, the
//     calendar page, the contacts table…). This hook deliberately does NOT
//     call initiateLoadEvents itself — the badge renders on every contact
//     page open, and a badge hook must never start fetches/listeners
//     (customer-page contract); TasksTab starts the listener on tab mount;
//   - cold-start fallback: the session-cache snapshot warmed by
//     preloadTasksTab on contacts-row hover (renders instantly, live listener
//     takes over on its first emission);
//   - live emissions are mirrored back into the session cache so reopening
//     the page (and the badge) stay warm.

import { useEffect, useMemo, useState } from "react";
import useAuth from "hooks/useAuth";
import useCalendar from "hooks/useCalendar";
import { getCached, setCached, subscribeCached } from "../../data/sessionCache";
import { contactTasksCacheKey, groupContactTasks, isEventOfContact } from "./contactTasksData";
import type { ContactTaskGroups } from "./contactTasksData";
import type { CalendarEvent, RealEstateContact } from "../../../../../../@types/sharedSchema";

export type ContactTasksState = {
  /** True only on a true cold start (no cache, live listener not emitted yet). */
  loading: boolean;
  /** True once CalendarContext's live events are available (enables the editor dialog). */
  live: boolean;
  tasks: CalendarEvent[];
  groups: ContactTaskGroups;
  openCount: number;
  overdueCount: number;
};

export function useContactTasks(contact: RealEstateContact): ContactTasksState {
  const { user } = useAuth();
  const { events } = useCalendar();

  const userEmail: string | undefined = user?.email ?? undefined;

  // Re-render when the preloaded snapshot lands in the session cache.
  const [, forceRender] = useState(0);
  useEffect(() => {
    if (!userEmail) return;
    return subscribeCached(contactTasksCacheKey(userEmail), () => forceRender((n) => n + 1));
  }, [userEmail]);

  // Mirror live emissions into the session cache (keeps future opens instant).
  useEffect(() => {
    if (events && userEmail) setCached(contactTasksCacheKey(userEmail), events);
  }, [events, userEmail]);

  const cachedEvents = userEmail
    ? getCached<CalendarEvent[]>(contactTasksCacheKey(userEmail))
    : undefined;

  const sourceEvents: CalendarEvent[] | undefined = events ?? cachedEvents;
  const live = !!events;
  const loading = sourceEvents === undefined;

  const contactEvents = useMemo(
    () => (sourceEvents ?? []).filter((event) => isEventOfContact(event, contact)),
    // The contact prop identity churns on live updates — key on the phone fields instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sourceEvents, contact.id, contact.contactId]
  );

  const tasks = useMemo(
    () => contactEvents.filter((event) => event.eventType === "task"),
    [contactEvents]
  );

  const groups = useMemo(() => groupContactTasks(tasks), [tasks]);

  const openCount =
    groups.overdue.length + groups.today.length + groups.thisWeek.length + groups.upcoming.length;

  return {
    loading,
    live,
    tasks,
    groups,
    openCount,
    overdueCount: groups.overdue.length
  };
}
